import { v4 as makeUuid } from 'uuid'

import appConfig from 'config/appConfig'

export interface AuthorizationUrlParams {
  state: string
  codeChallenge: string
}

export interface OAuthStatePayload {
  csrf: string
  returnPath?: string
}

export function generateState(returnPath?: string): string {
  const payload: OAuthStatePayload = { csrf: makeUuid(), returnPath }
  return btoa(JSON.stringify(payload))
}

export function parseState(state: string): OAuthStatePayload | null {
  try {
    const parsed = JSON.parse(atob(state))

    // Validate shape at runtime
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null
    }
    if (typeof parsed.csrf !== 'string') {
      return null
    }
    if (parsed.returnPath !== undefined && typeof parsed.returnPath !== 'string') {
      return null
    }

    // Return only allowlisted properties
    return {
      csrf: parsed.csrf,
      returnPath: parsed.returnPath
    }
  } catch {
    return null
  }
}

export function buildAuthorizationUrl(params: AuthorizationUrlParams): string {
  const { domain, clientId, redirectUri, scopes } = appConfig.BCC_LOGIN_APP

  if (!domain || !clientId || !redirectUri) {
    throw new Error('BCC Login App configuration is missing')
  }

  const searchParams = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes,
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256'
  })

  return `${domain}/oauth2/authorize?${searchParams.toString()}`
}

export function isTokenExpired(expiresAt?: number): boolean {
  if (!expiresAt) {
    return true
  }

  // Add a 60 second buffer before expiration
  const bufferMs = 60 * 1000
  return Date.now() >= expiresAt - bufferMs
}

export function calculateExpiresAt(expiresIn: number): number {
  return Date.now() + expiresIn * 1000
}

const BCC_AUTH_STORAGE_KEY = 'bcc_auth_state'
const BCC_TOKEN_STORAGE_KEY = 'bcc_access_token'
const BCC_EXPIRES_AT_STORAGE_KEY = 'bcc_expires_at'
const BCC_CODE_VERIFIER_STORAGE_KEY = 'bcc_code_verifier'
const BCC_SESSION_SECRET_STORAGE_KEY = 'bcc_session_secret'
const BCC_AUTH_DISMISSED_STORAGE_KEY = 'bcc_auth_dismissed'

/**
 * OAuth2 token storage in sessionStorage.
 *
 * SECURITY TRADE-OFF:
 * - sessionStorage is vulnerable to XSS attacks (malicious scripts can read tokens)
 * - HttpOnly cookies would be MORE secure (XSS-proof) but require backend changes
 *
 * Why sessionStorage was chosen:
 * 1. Tab isolation: prevents token leakage across browser tabs
 * 2. Auto-cleanup: cleared when browser tab closes
 * 3. Backend compatibility: current BCC OAuth2 service returns tokens in JSON
 *
 * ASSUMPTIONS (required for this approach to be acceptable):
 * - Content Security Policy (CSP) is properly configured to mitigate XSS
 * - All dependencies are regularly audited for security vulnerabilities
 * - Input sanitization is enforced throughout the application
 * - HTTPS is enforced (tokens never transmitted over HTTP)
 *
 * The OAuth2 state parameter provides CSRF protection independently of storage choice.
 *
 * FUTURE: Consider migrating to HttpOnly cookies when backend supports it.
 */

export function storeOAuthState(state: string): void {
  sessionStorage.setItem(BCC_AUTH_STORAGE_KEY, state)
}

export function getStoredOAuthState(): string | null {
  return sessionStorage.getItem(BCC_AUTH_STORAGE_KEY)
}

export function clearOAuthState(): void {
  sessionStorage.removeItem(BCC_AUTH_STORAGE_KEY)
}

export function storeAccessToken(token: string, expiresAt: number): void {
  sessionStorage.setItem(BCC_TOKEN_STORAGE_KEY, token)
  sessionStorage.setItem(BCC_EXPIRES_AT_STORAGE_KEY, expiresAt.toString())
}

export function getStoredAccessToken(): { token: string | null; expiresAt: number | null } {
  const token = sessionStorage.getItem(BCC_TOKEN_STORAGE_KEY)
  const expiresAtStr = sessionStorage.getItem(BCC_EXPIRES_AT_STORAGE_KEY)
  const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null

  return { token, expiresAt }
}

export function clearAccessToken(): void {
  sessionStorage.removeItem(BCC_TOKEN_STORAGE_KEY)
  sessionStorage.removeItem(BCC_EXPIRES_AT_STORAGE_KEY)
}

// PKCE functions

// Use 64-character charset (power of 2) for uniform distribution per RFC 7636
const PKCE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

export function generateCodeVerifier(): string {
  const randomValues = crypto.getRandomValues(new Uint8Array(128))
  return Array.from(randomValues, byte => PKCE_CHARSET[byte % 64]).join('')
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function storeCodeVerifier(verifier: string): void {
  sessionStorage.setItem(BCC_CODE_VERIFIER_STORAGE_KEY, verifier)
}

export function getStoredCodeVerifier(): string | null {
  return sessionStorage.getItem(BCC_CODE_VERIFIER_STORAGE_KEY)
}

export function clearCodeVerifier(): void {
  sessionStorage.removeItem(BCC_CODE_VERIFIER_STORAGE_KEY)
}

// Session secret persistence

export function storeSessionSecret(secret: string): void {
  sessionStorage.setItem(BCC_SESSION_SECRET_STORAGE_KEY, secret)
}

export function getStoredSessionSecret(): string | null {
  return sessionStorage.getItem(BCC_SESSION_SECRET_STORAGE_KEY)
}

export function clearSessionSecret(): void {
  sessionStorage.removeItem(BCC_SESSION_SECRET_STORAGE_KEY)
}

// Auth dismissed persistence

export function storeAuthDismissed(): void {
  sessionStorage.setItem(BCC_AUTH_DISMISSED_STORAGE_KEY, 'true')
}

export function getStoredAuthDismissed(): boolean {
  return sessionStorage.getItem(BCC_AUTH_DISMISSED_STORAGE_KEY) === 'true'
}

export function clearAuthDismissed(): void {
  sessionStorage.removeItem(BCC_AUTH_DISMISSED_STORAGE_KEY)
}

export function clearAllBccStorage(): void {
  sessionStorage.removeItem(BCC_AUTH_STORAGE_KEY)
  sessionStorage.removeItem(BCC_TOKEN_STORAGE_KEY)
  sessionStorage.removeItem(BCC_EXPIRES_AT_STORAGE_KEY)
  sessionStorage.removeItem(BCC_CODE_VERIFIER_STORAGE_KEY)
  sessionStorage.removeItem(BCC_SESSION_SECRET_STORAGE_KEY)
  sessionStorage.removeItem(BCC_AUTH_DISMISSED_STORAGE_KEY)
}
